import Edit16 from '@carbon/icons-react/es/edit/16';
import Minimize16 from '@carbon/icons-react/es/minimize/16';
import { Tooltip, Typography } from 'antd';
import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { ProjectId } from 'api/models';
import { ProjectPhase } from 'api/project';
import { FieldOption, StatusGroup } from 'api/tasks';
import { Button } from 'components/atoms/button';
import { ProductiveHeading06 } from 'components/styles';
import { PermissionRuleName } from 'constant';
import { useCurrentUserProjectAccess } from 'hooks/permissions';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { useTranslation } from 'hooks/use-translation';
import { ProjectPhaseModal } from '../../project-phase-modal';
import { PhaseStatusDropdown } from '../phase-status-dropdown';

const StyledText = styled(Typography.Text)`
  cursor: pointer;
`;

const HeaderWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const IconWrapper = styled.div`
  position: absolute;
  right: 16px;
  background-color: white;
  visibility: hidden;
  transition: visibility 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  padding-left: var(--spacing-02);
`;

const IconButton = styled(Button)`
  visibility: hidden;
`;

const Header = styled.div`
  align-items: center;
  background: var(--white-100);
  display: flex;
  height: 56px;
  padding: var(--spacing-04) var(--layout-01);
  position: relative;

  > .ant-dropdown-trigger {
    height: 32px;
    margin-right: var(--spacing-02);
  }

  && .ant-typography {
    ${ProductiveHeading06};
    flex: 1;
    margin-bottom: 0;
  }

  &:hover {
    ${IconWrapper}, ${IconButton} {
      visibility: visible;
    }
  }
`;

export const SwimlaneHeader: FC<{
  projectPhase: ProjectPhase;
  projectId: ProjectId;
  title: string;
  status: number;
  swimlaneState?: Record<number, boolean>;
  setSwimlaneState?: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
  onProjectPhaseChange(option: FieldOption<StatusGroup>): void;
}> = ({
  projectId,
  projectPhase,
  title,
  status,
  onProjectPhaseChange,
  swimlaneState,
  setSwimlaneState,
}) => {
  const [showEditPopover, toggleEditPopoverVisibility] = useState(false);
  const editProjectPhaseText = useTranslation('projects.phase.edit');
  const id = `phase-header-${projectPhase.projectPhaseId}`;
  const { isProjectAdmin } = useCurrentUserProjectAccess(projectId);
  const { isAllowedForCurrentUser } = usePermissionForRule();
  const hasPhaseEditAccess =
    isProjectAdmin ||
    isAllowedForCurrentUser(PermissionRuleName.ProjectPhaseEditAction);

  const onPhaseClick = () => {
    setSwimlaneState?.({
      ...swimlaneState,
      [projectPhase.projectPhaseId]: true,
    });
  };

  return (
    <Header id={id} data-cy={`phase-header${projectPhase.projectPhaseName}`}>
      <PhaseStatusDropdown
        disabled={!isProjectAdmin}
        status={status}
        onChange={onProjectPhaseChange}
      />
      <HeaderWrapper onClick={onPhaseClick}>
        <StyledText ellipsis={{ tooltip: true }}>{title}</StyledText>
      </HeaderWrapper>
      <IconWrapper>
        {swimlaneState ? (
          <IconButton
            size="small"
            type="text"
            icon={<Minimize16 />}
            onClick={onPhaseClick}
          />
        ) : null}
        {hasPhaseEditAccess ? (
          <>
            <Tooltip title={editProjectPhaseText}>
              <IconButton
                size="small"
                type="text"
                icon={<Edit16 />}
                onClick={() => toggleEditPopoverVisibility(true)}
                data-cy={`phase-header${projectPhase.projectPhaseName}.edit-phase`}
              />
            </Tooltip>
            <ProjectPhaseModal
              projectPhase={projectPhase}
              visible={showEditPopover}
              projectId={projectId}
              onClose={() => toggleEditPopoverVisibility(false)}
            />
          </>
        ) : null}
      </IconWrapper>
    </Header>
  );
};
